import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiPlay, FiPause } from "react-icons/fi";
import { Upscaler } from "../../video/upscale/gl";
import { Pills } from "../VideoStudioPanel";

const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

// Before and after, side by side in one picture: on the left the video stretched the way a browser normally does, on the right the upscaled picture;
// drag the line to compare, zoom in to see the detail. The video plays silently (and over and over) so the effect can be judged in motion.
export default function ComparePreview({ opened, out, look, makeEngine = (canvas) => new Upscaler(canvas), labels = ["Original, stretched", "Upscaled"], zooms = [1, 2, 4], pickMode = false, onPick = null }) {
  const { video, w: sw, h: sh, seconds } = opened;
  const canvasRef = useRef(null);
  const upRef = useRef(null);
  const [divider, setDivider] = useState(0.5);
  const [zoom, setZoom] = useState(1);
  const [focus, setFocus] = useState([0.5, 0.5]);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [problem, setProblem] = useState("");
  const dragging = useRef("");
  const state = useRef({});
  state.current = { divider, zoom, focus, look, out, labels };

  // the picture is made at the final size, up to 1920 pixels (enough to judge it; a 4K result is shown from this)
  const previewSize = useCallback(() => {
    const k = Math.min(1, 1920 / Math.max(out.w, out.h));
    return [Math.max(2, Math.round(out.w * k)), Math.max(2, Math.round(out.h * k))];
  }, [out.w, out.h]);

  useEffect(() => {
    try { upRef.current = makeEngine(document.createElement("canvas")); } catch (e) { setProblem(e.message); }
    return () => { if (upRef.current) upRef.current.dispose(); upRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- the engine is made once for the video

  const paint = useCallback(() => {
    const up = upRef.current;
    const canvas = canvasRef.current;
    if (!up || !canvas || video.readyState < 2) return;
    const { divider: d, zoom: z, focus: f, look: l, labels: names } = state.current;
    const [wanted, wantedH] = previewSize();
    try { up.setOutput(wanted, wantedH); up.render(video, l); } catch (e) { setProblem(e.message); return; }
    const pw = up.canvas.width;                                       // what the engine really drew (a noise reducer keeps the video's own size)
    const ph = up.canvas.height;
    const ctx = canvas.getContext("2d");
    const cw = canvas.width;
    const ch = canvas.height;
    const cropW = pw / z;
    const cropH = ph / z;
    const cx = clamp(f[0] * pw - cropW / 2, 0, pw - cropW);
    const cy = clamp(f[1] * ph - cropH / 2, 0, ph - cropH);
    const line = Math.round(d * cw);
    ctx.save();
    ctx.beginPath(); ctx.rect(line, 0, cw - line, ch); ctx.clip();
    ctx.drawImage(up.canvas, cx, cy, cropW, cropH, 0, 0, cw, ch);
    ctx.restore();
    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, line, ch); ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(video, (cx / pw) * sw, (cy / ph) * sh, (cropW / pw) * sw, (cropH / ph) * sh, 0, 0, cw, ch);
    ctx.restore();
    ctx.fillStyle = "#fff";
    ctx.fillRect(line - 1, 0, 2, ch);
    const u = cw / 960;                                              // the canvas is drawn at 960 pixels and shown smaller: sizes follow it
    ctx.beginPath(); ctx.arc(line, ch / 2, 18 * u, 0, Math.PI * 2); ctx.fillStyle = "#22d3ee"; ctx.fill();
    ctx.strokeStyle = "#fff"; ctx.lineWidth = 3 * u; ctx.stroke();
    ctx.fillStyle = "#06121f"; ctx.font = `700 ${Math.round(22 * u)}px sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("↔", line, ch / 2 + 1);
    const tag = (text, x, align) => {
      const size = Math.round(24 * u);
      ctx.font = `600 ${size}px sans-serif`; ctx.textAlign = align; ctx.textBaseline = "top";
      const width = ctx.measureText(text).width + size;
      ctx.fillStyle = "rgba(6,18,31,0.75)";
      ctx.fillRect(align === "left" ? x : x - width, 14 * u, width, size * 1.7);
      ctx.fillStyle = "#fff";
      ctx.fillText(text, align === "left" ? x + size / 2 : x - size / 2, 14 * u + size * 0.35);
    };
    if (line > 300 * u) tag(names[0], 14 * u, "left");
    if (cw - line > 180 * u) tag(names[1], cw - 14 * u, "right");
  }, [video, sw, sh, previewSize]);

  // an engine that must ask something first (the AI model of the background changer) is waited for; a request that comes meanwhile is answered once afterwards
  const preparing = useRef(false);
  const wantAgain = useRef(false);
  const drawRef = useRef(null);
  const draw = useCallback(async () => {
    const up = upRef.current;
    if (up && up.prepare && video.readyState >= 2) {
      if (preparing.current) { wantAgain.current = true; return; }
      preparing.current = true;
      try { await up.prepare(video, state.current.look); } catch (e) { setProblem(e.message); return; } finally { preparing.current = false; }
      if (!upRef.current) return;
    }
    paint();
    if (wantAgain.current) { wantAgain.current = false; setTimeout(() => drawRef.current && drawRef.current(), 0); }
  }, [paint, video]);
  drawRef.current = draw;

  // draw again whenever something that shows changes, and while the video plays
  useEffect(() => { draw(); }, [draw, divider, zoom, focus, look, out]);
  useEffect(() => {
    const again = () => { draw(); setTime(video.currentTime); };
    video.addEventListener("seeked", again);
    video.addEventListener("loadeddata", again);
    return () => { video.removeEventListener("seeked", again); video.removeEventListener("loadeddata", again); };
  }, [video, draw]);
  useEffect(() => {
    if (!playing) return undefined;
    let stop = false;
    let id = 0;
    const tick = () => {
      if (stop) return;
      draw();
      setTime(video.currentTime);
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(tick); else id = requestAnimationFrame(tick);
    };
    tick();
    return () => { stop = true; cancelAnimationFrame(id); };
  }, [playing, video, draw]);
  useEffect(() => {
    video.muted = true;
    video.loop = true;
    return () => { video.pause(); };
  }, [video]);

  const toggle = () => {
    if (playing) { video.pause(); setPlaying(false); } else { video.play().then(() => setPlaying(true)).catch(() => {}); }
  };
  const where = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return [clamp((e.clientX - rect.left) / rect.width, 0, 1), clamp((e.clientY - rect.top) / rect.height, 0, 1)];
  };
  const down = (e) => {
    const [x, y] = where(e);
    if (pickMode && onPick) {                                           // a click that picks a spot of the video (for example the colour of a green screen)
      const up = upRef.current;
      const pw = up ? up.canvas.width : sw;
      const ph = up ? up.canvas.height : sh;
      const cropW = pw / zoom;
      const cropH = ph / zoom;
      const cx = clamp(focus[0] * pw - cropW / 2, 0, pw - cropW);
      const cy = clamp(focus[1] * ph - cropH / 2, 0, ph - cropH);
      onPick((cx + x * cropW) / pw, (cy + y * cropH) / ph);
      return;
    }
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* a pointer that is no longer there: dragging still works without it */ }
    if (Math.abs(x - divider) < 0.05) dragging.current = "divider";
    else if (zoom > 1) { dragging.current = "focus"; setFocus([x, y]); }
    else { dragging.current = "divider"; setDivider(x); }
  };
  const move = (e) => {
    if (!dragging.current) return;
    const [x, y] = where(e);
    if (dragging.current === "divider") setDivider(clamp(x, 0.02, 0.98)); else setFocus([x, y]);
  };
  const release = () => { dragging.current = ""; };

  const aspect = out.w / out.h;
  const cw = aspect >= 1 ? 960 : Math.round(720 * aspect);
  const ch = aspect >= 1 ? Math.round(960 / aspect) : 720;
  if (problem) return <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">{problem}</p>;
  return (
    <div>
      <div className="rounded-xl overflow-hidden bg-black flex justify-center">
        <canvas ref={canvasRef} width={cw} height={ch} onPointerDown={down} onPointerMove={move} onPointerUp={release} onPointerCancel={release}
          className={`block max-w-full max-h-[560px] w-auto touch-none ${pickMode ? "cursor-crosshair" : "cursor-ew-resize"}`} style={{ aspectRatio: `${cw} / ${ch}` }} aria-label="Before and after comparison" />
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button type="button" onClick={toggle} className="w-10 h-10 rounded-full bg-brand-gradient text-white flex items-center justify-center shrink-0" aria-label={playing ? "Pause the preview" : "Play the preview"}>{playing ? <FiPause /> : <FiPlay />}</button>
        <input type="range" min="0" max={Math.max(0.1, seconds)} step="0.05" value={Math.min(time, seconds || 0.1)} aria-label="Position in the preview"
          onChange={(e) => { const t = Number(e.target.value); video.currentTime = t; setTime(t); }} className="flex-1 accent-[#22d3ee]" />
        <Pills label="Zoom" value={String(zoom)} onChange={(v) => setZoom(Number(v))} options={zooms.map((z) => [String(z), z === 1 ? "Fit" : `${z}×`])} />
      </div>
      <p className="text-xs text-gray-500 mt-2">Drag the line to compare. {zoom > 1 ? "Click the picture to look at another spot." : "Zoom in to see the detail."} The preview is silent.</p>
    </div>
  );
}
